import { useId, type ReactNode } from "react";
import HoverHint from "../feedback/HoverHint";
import { FORM_HELP_TEXT_CLASS, FORM_LABEL_WITH_SPACING_CLASS } from "../styles/formStyles";
import { classNames } from "../styles/classNames";

interface FieldGroupBase {
  /** Reads the legend to screen readers only, where the group's place already names it on screen. */
  legendHidden?: boolean;
  hint?: ReactNode;
  disabled?: boolean;
  /** Layout of the options (gap, columns); the legend looks like every field label. */
  className?: string;
  children: ReactNode;
}

type FieldGroupProps = FieldGroupBase & (
  | { /** Names the group: read before each of its options. */ legend: ReactNode; explanation?: never }
  | {
    legend: string;
    /** What the choice does, on hover or focus beside the legend (HoverHint); the group's
     * name stays the legend alone. */
    explanation?: ReactNode;
  }
);

/** A group of options or fields under one name, such as radio buttons for one choice. */
export default function FieldGroup({ legend, legendHidden = false, hint, explanation, disabled, className, children }: FieldGroupProps) {
  const nameId = useId();
  const explained = explanation !== undefined && typeof legend === "string";
  return (
    <fieldset className={classNames("space-y-1 text-sm", className)} disabled={disabled} aria-labelledby={explained ? nameId : undefined}>
      <legend className={legendHidden ? "sr-only" : FORM_LABEL_WITH_SPACING_CLASS}>
        {explained ? <span className="inline-flex items-center gap-1"><span id={nameId}>{legend}</span><HoverHint name={legend}>{explanation}</HoverHint></span> : legend}
      </legend>
      {hint === undefined ? null : <p className={FORM_HELP_TEXT_CLASS}>{hint}</p>}
      {children}
    </fieldset>
  );
}
